import { useEffect, useMemo } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Phone, MessageSquare, Zap, CheckCircle2, Clock, Shield } from "lucide-react";
import LeadForm from "@/components/LeadForm";
import Disclosure from "@/components/Disclosure";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import { BUSINESS_PHONE, BUSINESS_PHONE_FORMATTED, BRAND, OWNER_FIRST_NAME } from "@/lib/constants";

// Channel-specific landing variants.
// Each mirrors the ad's offer and tone and sets utm_source automatically.
const CHANNELS = {
    tiktok: {
        chip: "TikTok · 60 seconds to compare",
        headline: "Still paying 18¢/kWh?",
        highlight: "Let's fix that.",
        subhead: "Drop your ZIP, I'll check what's really available in your area. No pressure, same lights, lower rates where I can.",
        bullets: [
            "60-second request, no sales pitch",
            "Real texts back from Jeffrey",
            "Works for apartments & houses",
            "Spanish friendly",
        ],
        imageUrl: "https://images.unsplash.com/photo-1758687126877-b37052a20a4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGZhbWlseSUyMGhvbWUlMjBlbmVyZ3klMjBzYXZpbmdzfGVufDB8fHx8MTc5MDk5OTExM3ww&ixlib=rb-4.1.0&q=85",
        seoTitle: "Lower your light bill - TikTok",
    },
    facebook: {
        chip: "Facebook · For families & homeowners",
        headline: "Your electric bill is quietly eating your paycheck.",
        highlight: "Let me check your options.",
        subhead: "I'm Jeffrey - an independent rep helping Texas families compare plans. Same service, lower rates when available. Family-first, zero pressure.",
        bullets: [
            "Homeowners & renters welcome",
            "Fixed-rate or no contract options",
            "Jeffrey, real human, Texas",
            "Only takes a few minutes",
        ],
        imageUrl: "https://images.unsplash.com/photo-1622790410882-c1f00a56083e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGZhbWlseSUyMGhvbWUlMjBlbmVyZ3klMjBzYXZpbmdzfGVufDB8fHx8MTc5MDk5OTExM3ww&ixlib=rb-4.1.0&q=85",
        seoTitle: "Compare electricity plans for your family - Facebook",
    },
    google: {
        chip: "Google Ads · Houston & all of Texas",
        headline: "Compare Texas electricity plans",
        highlight: "in under 2 minutes.",
        subhead: "Share your ZIP and preferences. We review real available options in your utility territory and follow up only how you choose. No fake rates, no pressure.",
        bullets: [
            "CenterPoint, Oncor, AEP, TNMP territories",
            "Residential & commercial",
            "No deposit options available",
            "Independent representative",
        ],
        imageUrl: "https://images.pexels.com/photos/3584283/pexels-photo-3584283.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        seoTitle: "Compare Texas electricity plans - Google Ads",
    },
    instagram: {
        chip: "Instagram · DM-friendly",
        headline: "Save on your light bill.",
        highlight: "Text me, I got you.",
        subhead: "Pull up with your ZIP code - I'll check what plans are available. No pressure, no spam, straight answers.",
        bullets: [
            "Text or DM-friendly",
            "Apartment-friendly",
            "First-time setup help",
            "Spanish or English",
        ],
        imageUrl: "https://images.unsplash.com/photo-1622790410882-c1f00a56083e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGZhbWlseSUyMGhvbWUlMjBlbmVyZ3klMjBzYXZpbmdzfGVufDB8fHx8MTc5MDk5OTExM3ww&ixlib=rb-4.1.0&q=85",
        seoTitle: "Save on your light bill - Instagram",
    },
};

export default function AdLandingPage() {
    const { channel } = useParams();
    const cfg = useMemo(() => CHANNELS[channel?.toLowerCase()], [channel]);

    useEffect(() => {
        if (cfg?.seoTitle) document.title = `${cfg.seoTitle} · ${BRAND}`;
    }, [cfg]);

    if (!cfg) return <Navigate to="/get-started" replace />;

    return (
        <div className="min-h-screen" data-testid={`ad-landing-${channel}`}>
            {/* Minimal top bar — no main nav to reduce distractions */}
            <header className="sticky top-0 z-30 glass">
                <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <span className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-500/40 inline-flex items-center justify-center text-emerald-300">
                            <Zap className="w-5 h-5" />
                        </span>
                        <span className="font-extrabold text-white">{BRAND}</span>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <a
                            href={`tel:${BUSINESS_PHONE}`}
                            className="hidden sm:inline-flex items-center gap-1.5 text-sm text-emerald-200 border border-white/10 rounded-full px-3 py-1.5"
                            data-testid="ad-top-call"
                        >
                            <Phone className="w-4 h-4" /> {BUSINESS_PHONE_FORMATTED}
                        </a>
                        <a
                            href={`sms:${BUSINESS_PHONE}?&body=Hi ${OWNER_FIRST_NAME}, text me my options`}
                            className="btn-primary !py-2 !min-h-0 text-sm"
                            data-testid="ad-top-text"
                        >
                            <MessageSquare className="w-4 h-4" /> Text {OWNER_FIRST_NAME}
                        </a>
                    </div>
                </div>
            </header>

            <section className="relative">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="hero-glow -top-20 -left-20" />
                    <div className="absolute inset-0 spark-pattern opacity-25" />
                </div>
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-6">
                    <span className="chip reveal" data-testid="ad-chip">{cfg.chip}</span>
                    <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white reveal d1">
                        {cfg.headline}{" "}
                        <span className="accent-gradient-text">{cfg.highlight}</span>
                    </h1>
                    <p className="mt-4 text-lg text-slate-300 max-w-2xl reveal d2">{cfg.subhead}</p>

                    <div className="mt-6 grid sm:grid-cols-2 gap-2 reveal d3 max-w-2xl">
                        {cfg.bullets.map((b, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm text-slate-300">
                                <span className="w-5 h-5 mt-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 inline-flex items-center justify-center text-emerald-300 text-xs">✓</span>
                                {b}
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3 reveal d4">
                        <a href={`tel:${BUSINESS_PHONE}`} className="btn-ghost" data-testid="ad-hero-call">
                            <Phone className="w-4 h-4" /> Call {BUSINESS_PHONE_FORMATTED}
                        </a>
                        <a
                            href={`sms:${BUSINESS_PHONE}?&body=Hi ${OWNER_FIRST_NAME}, text me my options`}
                            className="btn-primary"
                            data-testid="ad-hero-text"
                        >
                            <MessageSquare className="w-4 h-4" /> Text me my options
                        </a>
                    </div>

                    <div className="mt-6 flex items-center gap-4 text-xs text-slate-400">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> ~60 sec</span>
                        <span className="inline-flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> No pressure</span>
                        <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Real human</span>
                    </div>
                </div>
            </section>

            <section className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="ad-form-section">
                <LeadForm source={`lp-${channel}`} initial={{ utm_source: channel }} />
            </section>

            <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-10">
                <Disclosure />
            </section>

            <StickyMobileCTA />
        </div>
    );
}
